import { useParams } from 'react-router';
import { useTabs, useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import { Tab, Tabs, Skeleton, Typography } from '@mui/material';

import { usePolicyById } from 'src/api/hooks/usePolicyQueries';

import { PolicyStatusChips } from 'src/sections/employer-data/shared/components/status-chips';

import { showError } from '../shared/utils/policyUtils';
import { PolicyGeneral } from './general/policy-general';
import { getPrimaryPolicyName } from '../shared/utils/policyName';
import DynamicBreadcrumbs from '../dynamic-breadcrumbs/dynamic-breadcrumbs';
import { PolicyRatingHistory } from './rating-history/policy-rating-history';
import { MovePolicyButton } from './components/move-policy-button';
import { MovePolicyModal } from './components/move-policy-modal';
import { PolicyCarrierProfile } from './carrier-profile/policy-carrier-profile';

// ----------------------------------------------------------------------

export function Policy() {
  const tabs = useTabs('general');
  const movePolicyModal = useBoolean();
  const { comboId, coverageId, policyId } = useParams<{
    comboId: string;
    coverageId: string;
    policyId: string;
  }>();

  const { data, error, isError, isLoading, isPending, isSuccess } = usePolicyById(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );
  if (isLoading || isPending) {
    return <Skeleton variant="text" sx={{ fontSize: '1rem' }} data-testid="skeleton" />;
  }
  if (isError || !isSuccess || !data?.data) {
    return showError(policyId, error?.message ?? 'Unknown error');
  }

  const viewPolicy = data?.data;

  const ED_POLICY_TABS = [
    {
      value: 'general',
      label: 'General',
      component: <PolicyGeneral />,
      'data-testid': 'tab-general',
    },
    {
      value: 'ratinghistory',
      label: 'Rating history',
      component: <PolicyRatingHistory viewPolicy={viewPolicy} />,
      'data-testid': 'tab-ratinghistory',
    },
    {
      value: 'communications',
      label: 'Communications',
      component: <Box />,
      'data-testid': 'tab-communications',
    },
    {
      value: 'carrierprofile',
      label: 'Carrier profile',
      component: (
        <PolicyCarrierProfile
          carrier={viewPolicy?.carrier}
          carrierContacts={viewPolicy?.carrierContacts}
          isLoading={isLoading || isPending}
        />
      ),
      'data-testid': 'tab-carrierprofile',
    },
    { value: 'changelog', label: 'Change log', component: <Box />, 'data-testid': 'tab-changelog' },
  ];

  const primaryPolicyName = getPrimaryPolicyName(viewPolicy?.policyNames ?? undefined);

  const renderContent = () => (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h4">
            {primaryPolicyName !== undefined
              ? `${primaryPolicyName.insuredName} - ${viewPolicy?.policyNumber}`
              : 'Policy Page'}
            <PolicyStatusChips
              isActive={viewPolicy?.isActive}
              isCancelled={viewPolicy?.isCancelled}
              isNonCompliant={viewPolicy?.isNonCompliant}
            />
          </Typography>
          <MovePolicyButton onClick={movePolicyModal.onTrue} />
        </Box>
        <DynamicBreadcrumbs crumbStartPath="combo" />
        <Tabs
          value={tabs.value}
          onChange={tabs.onChange}
          aria-label="employer data policy tabs"
          data-testid="employer-data-policy-tabs"
        >
          {ED_POLICY_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              data-testid={tab['data-testid']}
            />
          ))}
        </Tabs>
      </Box>
      {ED_POLICY_TABS.map(
        (tab) => tab.value === tabs.value && <Box key={tab.value}>{tab.component}</Box>
      )}
      <MovePolicyModal
        open={movePolicyModal.value}
        onClose={movePolicyModal.onFalse}
        comboId={Number(comboId)}
        coverageId={Number(coverageId)}
        policyId={Number(policyId)}
      />
    </Box>
  );

  return <>{renderContent()}</>;
}
